import { FloorplanLevel } from '../../../types/floorplan';

/**
 * Desk naming for the floor plan panels and dialogs. People know a desk by
 * the number painted on the canvas ("Desk 102"), never by its internal key
 * ("floor1-desk-102-123c54c2"). The key is only a fallback for records that
 * arrive without a number (older activity entries).
 *
 * These live in the floorplan-panels group folder because components/floorplan/utils.ts
 * belongs to another group; they can move there later without changing callers' output.
 */
type DeskRef = { number?: string | null; key?: string | null } | null | undefined;

/** "102" — the number people see on the canvas, or '' when the desk has neither. */
export const deskNumberOf = (desk: DeskRef): string => desk?.number?.trim() || desk?.key?.trim() || '';

/** "Desk 102" — a title or a standalone label. */
export const deskLabel = (desk: DeskRef): string => {
  const number = deskNumberOf(desk);
  return number ? `Desk ${number}` : 'Desk';
};

/** "desk 102" — inside a sentence or an accessible name ("Assign Jane to desk 102"). */
export const deskInSentence = (desk: DeskRef): string => {
  const number = deskNumberOf(desk);
  return number ? `desk ${number}` : 'this desk';
};

/**
 * Floor names keyed by level id, but only when the layout has more than one
 * floor: on a single-floor layout the floor name adds nothing.
 */
export const floorNamesWhenMixed = (levels: FloorplanLevel[] | undefined): Map<string, string> =>
  new Map(levels && levels.length > 1 ? levels.map((level) => [level.id, level.name]) : []);

/** Floor names keyed by desk id (for records, like activity, that carry no level id). Mixed layouts only. */
export const floorNameByDeskId = (levels: FloorplanLevel[] | undefined): Map<string, string> => {
  const result = new Map<string, string>();
  if (!levels || levels.length < 2) return result;
  levels.forEach((level) => level.desks.forEach((desk) => result.set(desk.id, level.name)));
  return result;
};

/** "Desk 102 · 4th Floor", or "Desk 102" when there is no floor to name. */
export const deskPlaceLabel = (desk: DeskRef, floorName?: string | null): string =>
  [deskLabel(desk), floorName || null].filter(Boolean).join(' · ');
